import React from "react";
import { Link } from "react-router";
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  CheckCircle2,
  DollarSign,
  Flag,
  Landmark,
  Scale,
  Sparkles,
} from "lucide-react";
import {
  ArticleCard,
  FAQItem,
  LegalTopicIntroHero,
  MediaCarousel,
  PodcastCard,
  SectionHead,
  VideoCard,
  foregroundOnSolidAccent,
} from "../shared/categoryDetailShared";

const ListBlock = ({ items, color, icon: Icon = CheckCircle2, onDark = false }) => (
  <ul className="space-y-2.5">
    {items.map((line) => (
      <li
        key={line}
        className={`flex gap-3 text-sm font-semibold leading-relaxed sm:text-[0.9375rem] ${
          onDark ? "text-white" : "text-gray-800"
        }`}
      >
        <span
          className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${onDark ? "ring-1 ring-white/20" : ""}`}
          style={{ background: onDark ? `${color}45` : `${color}14`, color }}
        >
          <Icon size={14} strokeWidth={2.25} />
        </span>
        <span className="min-w-0 pt-0.5">{line}</span>
      </li>
    ))}
  </ul>
);

/** Overview — Debt & financial legal issues (Act 1052, recovery, ADR, insolvency). */
export const DebtFinancialLegalOverviewTab = ({ data, color }) => (
  <div className="space-y-10">
    <LegalTopicIntroHero
      color={color}
      categoryEyebrow="LEGAL · DEBT &amp; FINANCIAL ISSUES (GHANA)"
      funnelTagline={data.funnelTagline}
      FunnelIcon={DollarSign}
      gallerySrc={data.galleryImages[0]}
      imageBandIcon={Landmark}
      imageBandLabel="Recovery · fairness · timelines"
      imageSubtitle="Proof in hand · BoG ladders · courts or ADR—lawfully."
      statsPreview={data.stats}
    >
      <h2 className="max-w-2xl text-[1.6rem] font-black leading-[1.14] tracking-[-0.02em] text-slate-950 sm:text-[1.95rem] lg:text-[2.125rem]">
        {data.introduction.headline}
      </h2>
      <p className="mt-5 max-w-prose text-base leading-relaxed text-slate-600 sm:text-[1.05rem] sm:leading-relaxed">{data.introduction.lead}</p>

      <div
        className="mt-5 rounded-2xl border border-slate-200/95 bg-white/85 px-5 py-4 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.9)] backdrop-blur-sm sm:px-6 sm:py-5"
        style={{ borderLeftWidth: "4px", borderLeftColor: color }}
      >
        <p className="text-sm font-semibold leading-relaxed text-slate-800 sm:text-[0.9375rem]">{data.introduction.problemStarts}</p>
      </div>

      <div className="mt-8 rounded-2xl border border-slate-200/85 bg-white/95 p-5 shadow-[0_22px_50px_-40px_rgba(15,23,42,0.35)] sm:p-7">
        <p className="text-[11px] font-black uppercase tracking-[0.26em] text-slate-500">{data.introduction.frameworkLead}</p>
        <ul className="mt-4 space-y-3 border-t border-slate-100/90 pt-4">
          {data.introduction.frameworkBullets.map((f) => (
            <li key={f} className="flex gap-3 text-sm font-semibold leading-relaxed text-slate-800 sm:text-[0.9375rem]">
              <span
                className="mt-[0.35rem] inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ backgroundColor: color, boxShadow: `0 0 0 3px ${color}22` }}
                aria-hidden
              />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {data.focusAreas.map((tag) => (
          <span
            key={tag}
            className="rounded-xl border border-slate-200/90 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700 shadow-sm transition hover:border-slate-300/95"
          >
            {tag}
          </span>
        ))}
      </div>
    </LegalTopicIntroHero>

    <section>
      <SectionHead eyebrow="High intent" title={data.miniSiteGoals.title} subtitle={data.miniSiteGoals.subtitle} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <div className="grid gap-2 md:grid-cols-2">
          {data.miniSiteGoals.questions.map((q) => (
            <div key={q} className="rounded-lg border border-slate-100 bg-slate-50/80 px-4 py-3 text-sm font-semibold text-slate-900">
              • {q}
            </div>
          ))}
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Statute stack" title={data.frameworkDetail.title} color={color} />
      <div className="grid gap-4 md:grid-cols-2">
        {data.frameworkDetail.items.map((item) => (
          <div key={item.title} className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm">
            <p className="text-sm font-black text-slate-950">{item.title}</p>
            <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-600">{item.body}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <SectionHead eyebrow="Strategy" title={data.debtRecovery.title} color={color} />
        <p className="-mt-2 mb-6 text-sm font-semibold text-slate-600">{data.debtRecovery.lead}</p>
        <ListBlock items={data.debtRecovery.channels} color={color} icon={Banknote} />
        <p className="mt-8 text-xs font-black uppercase tracking-wider" style={{ color }}>
          {data.debtRecovery.proofHeading}
        </p>
        <ul className="mt-3 space-y-1 text-sm font-semibold text-slate-800">
          {data.debtRecovery.proofBullets.map((p) => (
            <li key={p}>• {p}</li>
          ))}
        </ul>
      </div>
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.recoveryFlow.title}</p>
        <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm font-semibold text-slate-800">
          {data.recoveryFlow.steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
        <div className="mt-8 rounded-xl border border-emerald-200/75 bg-emerald-50/40 p-5">
          <p className="text-sm font-semibold text-emerald-950">{data.recoveryFlow.note}</p>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Litigation lane" title={data.courtRecovery.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="max-w-prose text-sm font-semibold leading-relaxed text-slate-800">{data.courtRecovery.body}</p>
        <p className="mt-6 rounded-xl border border-blue-100 bg-blue-50/45 p-5 text-sm font-semibold text-blue-950">{data.courtRecovery.signal}</p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Friction" title={data.loanDisputes.title} color={color} />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">Disagreement points</p>
          <ul className="mt-3 space-y-1 text-sm font-semibold text-slate-800">
            {data.loanDisputes.disagreements.map((d) => (
              <li key={d}>• {d}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-amber-200/75 bg-amber-50/40 p-6">
          <p className="text-xs font-black uppercase tracking-wider text-amber-950">Ghana-pattern risks</p>
          <ul className="mt-3 space-y-1 text-sm font-semibold text-amber-950/95">
            {data.loanDisputes.ghanaFriction.map((g) => (
              <li key={g}>• {g}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-900/[0.08] bg-slate-950 p-6 text-white">
          <p className="text-sm font-semibold leading-relaxed text-white/85">{data.loanDisputes.bogNote}</p>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Before you sign" title={data.borrowerChecklist.title} color={color} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <ul className="grid gap-2 sm:grid-cols-2">
            {data.borrowerChecklist.items.map((item) => (
              <li key={item} className="rounded-lg bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800">
                • {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col justify-center rounded-xl border-2 border-red-200/80 bg-red-50/40 p-6 sm:p-8">
          <p className="flex items-start gap-2 text-sm font-black leading-snug text-red-950">
            <AlertTriangle size={20} className="mt-0.5 shrink-0 text-red-600" aria-hidden />
            {data.borrowerChecklist.warning}
          </p>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Security" title={data.collateralSection.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <ul className="space-y-3 text-sm font-semibold text-slate-800">
          {data.collateralSection.truths.map((t) => (
            <li key={t}>• {t}</li>
          ))}
        </ul>
        <p className="mt-8 rounded-xl border p-5 text-sm font-bold text-slate-900" style={{ borderColor: `${color}40`, background: `${color}08` }}>
          {data.collateralSection.balance}
        </p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="No bank involved" title={data.informalDebt.title} color={color} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6">
          <ul className="flex flex-wrap gap-2">
            {data.informalDebt.pairs.map((p) => (
              <li key={p} className="rounded-full border border-slate-200 px-3 py-1 text-xs font-bold text-slate-700">
                {p}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm font-semibold text-slate-700">{data.informalDebt.pain}</p>
        </div>
        <div className="rounded-xl border border-emerald-200/75 bg-emerald-50/40 p-6">
          <p className="text-xs font-black uppercase tracking-wider text-emerald-950">Evidence that saves cases</p>
          <ul className="mt-3 space-y-1 text-sm font-semibold text-emerald-950/95">
            {data.informalDebt.evidence.split(", ").map((e) => (
              <li key={e}>• {e}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <SectionHead eyebrow="Creditor" title={data.ifOwedMoney.title} color={color} />
        <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm font-semibold text-slate-900">
          {data.ifOwedMoney.steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
        <p className="mt-8 inline-flex items-start gap-2 text-sm font-black text-slate-950">
          <Scale size={18} style={{ color }} className="mt-0.5 shrink-0" aria-hidden />
          {data.ifOwedMoney.mantra}
        </p>
      </div>
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.ifPressured.title}</p>
        <div className="mt-6">
          <ListBlock items={data.ifPressured.moves} color={color} />
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Regulator" title={data.consumerComplaints.title} color={color} />
      <div className="rounded-xl border border-indigo-200/70 bg-indigo-50/40 p-6 sm:p-8">
        <p className="text-xs font-black uppercase tracking-wider text-indigo-950">Bank of Ghana flags</p>
        <ul className="mt-3 space-y-1 text-sm font-semibold text-indigo-950/95">
          {data.consumerComplaints.bogExamples.map((b) => (
            <li key={b}>• {b}</li>
          ))}
        </ul>
        <p className="mt-6 text-sm font-bold text-indigo-950">{data.consumerComplaints.takeaway}</p>
      </div>
    </section>

    <section className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <SectionHead eyebrow="Structural relief" title={data.insolvencySection.title} subtitle="Act 708 framing" color={color} />
        <p className="-mt-2 mb-6 text-sm font-semibold text-slate-600">{data.insolvencySection.lead}</p>
        <p className="text-xs font-black uppercase tracking-wider text-slate-500">When teams explore petitions</p>
        <ul className="mt-3 space-y-1 text-sm font-semibold text-slate-800">
          {data.insolvencySection.when.map((w) => (
            <li key={w}>• {w}</li>
          ))}
        </ul>
        <p className="mt-8 text-sm font-bold text-red-900">{data.insolvencySection.caution}</p>
      </div>
      <div className="rounded-xl border border-slate-900/[0.08] bg-slate-950 p-6 text-white sm:p-8">
        <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
          ADR
        </p>
        <h3 className="mt-2 text-xl font-black">{data.adrSection.title}</h3>
        <ListBlock items={data.adrSection.fit} color={color} icon={Sparkles} onDark />
        <p className="mt-6 text-sm font-semibold text-white/75">{data.adrSection.point}</p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Reality" title={data.commonProblems.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <ul className="grid gap-2 sm:grid-cols-2">
          {data.commonProblems.items.map((i) => (
            <li key={i} className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900">
              • {i}
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Playbook" title={data.practicalTips.title} color={color} />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[data.practicalTips.beforeBorrow, data.practicalTips.beforeLend, data.practicalTips.dispute, data.practicalTips.regulated].map((block) => (
          <div key={block.heading} className="rounded-xl border border-slate-200/90 bg-white p-6 shadow-sm">
            <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
              {block.heading}
            </p>
            <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-700">
              {block.items.map((it) => (
                <li key={it}>• {it}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Warnings" title={data.redFlags.title} color={color} />
      <div className="rounded-xl border border-red-200/70 bg-red-50/35 p-6 sm:p-8">
        <ul className="space-y-3">
          {data.redFlags.items.map((r) => (
            <li key={r} className="flex gap-3 text-sm font-semibold text-red-950">
              <Flag size={16} className="mt-0.5 shrink-0 text-red-500" aria-hidden />
              {r}
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Content funnel" title={data.editorialPlaybook.title} color={color} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[
          { label: "Recovery", items: data.editorialPlaybook.recoveryPages },
          { label: "Loans", items: data.editorialPlaybook.loanPages },
          { label: "Collateral", items: data.editorialPlaybook.collateralPages },
          { label: "Insolvency", items: data.editorialPlaybook.insolvencyPages },
          { label: "ADR", items: data.editorialPlaybook.adrPages },
        ].map((col) => (
          <div key={col.label} className="rounded-xl border border-slate-200/90 bg-white p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{col.label}</p>
            <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-800">
              {col.items.map((item) => (
                <li key={item}>• {item}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Funnel" title={data.funnelApplication.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.funnelApplication.hooksHeading}</p>
        <ul className="mt-3 space-y-2">
          {data.funnelApplication.hooks.map((h) => (
            <li key={h} className="text-sm font-bold text-slate-900">
              • {h}
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {[data.funnelApplication.education, data.funnelApplication.authority, data.funnelApplication.conversion].map((step) => (
          <div key={step.label} className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm">
            <p className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color }}>
              {step.label}
            </p>
            <p className="mt-3 text-sm font-bold leading-relaxed text-slate-900">{step.text}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="relative overflow-hidden rounded-xl border border-slate-900/10 bg-slate-950 p-8 text-white sm:p-10">
      <div
        className="absolute inset-0 opacity-30 blur-3xl"
        style={{ background: `radial-gradient(circle at 30% 20%, ${color}, transparent 55%)` }}
        aria-hidden
      />
      <div className="relative">
        <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
          {data.finalTakeaway.title}
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {data.finalTakeaway.pillars.map((p) => (
            <span key={p} className="rounded-full border border-white/20 px-4 py-1.5 text-xs font-black text-white/95">
              {p}
            </span>
          ))}
        </div>
        <p className="mt-8 text-xs font-black uppercase tracking-wider text-white/50">{data.finalTakeaway.messageLead}</p>
        <ul className="mt-3 space-y-2 text-sm font-semibold text-white/85">
          {data.finalTakeaway.mistakes.map((m) => (
            <li key={m}>• {m}</li>
          ))}
        </ul>
        <p className="mt-8 flex items-start gap-2 text-base font-black leading-snug text-white">
          <CheckCircle2 size={22} className="mt-1 shrink-0 opacity-90" style={{ color }} aria-hidden />
          {data.finalTakeaway.safeMessage}
        </p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Summary" title={data.bottomLine.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-8 text-center shadow-sm">
        <p className="text-lg font-black text-slate-950">{data.bottomLine.lead}</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
          <p className="rounded-lg border border-emerald-200/80 bg-emerald-50/50 p-4 text-sm font-bold text-emerald-950">{data.bottomLine.smooth}</p>
          <span className="text-sm font-black text-slate-400">{data.bottomLine.and}</span>
          <p className="rounded-lg border border-orange-200/80 bg-orange-50/55 p-4 text-sm font-bold text-orange-950">{data.bottomLine.stressful}</p>
        </div>
        <p className="mt-6 inline-flex flex-wrap items-center justify-center gap-2 text-sm font-semibold text-slate-600">
          <ArrowRight size={16} style={{ color }} aria-hidden />
          {data.bottomLine.closing}
        </p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Frequently Asked" title="Most-Asked Questions" color={color} />
      <div className="space-y-2">
        {data.faqs.map((faq) => (
          <FAQItem key={faq.q} q={faq.q} a={faq.a} color={color} />
        ))}
      </div>
    </section>
  </div>
);

export const DebtFinancialLegalArticlesTab = ({ data, color, industryId, slug, ctaTo }) => (
  <div className="space-y-10">
    <section>
      <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <SectionHead eyebrow="Library" title="Articles &amp; Guides" subtitle={data.funnelTagline} color={color} />
        <span className="mb-8 shrink-0 rounded-lg px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {data.articles.length} articles
        </span>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data.articles.map((article) => (
          <ArticleCard key={article.id} article={article} to={`/${industryId}/${slug}/${article.slug}`} color={color} />
        ))}
      </div>
    </section>

    <section
      className="overflow-hidden rounded-xl p-8 sm:p-10"
      style={{
        background: `linear-gradient(128deg, ${color}12, transparent)`,
        border: `1px solid ${color}35`,
      }}
    >
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
            {data.editorialPlaybook.leadCtasHeading}
          </p>
          <h3 className="text-xl font-black text-slate-950 sm:text-2xl">{data.primaryCtaHeading}</h3>
          <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-600">
            {data.editorialPlaybook.leadCtas.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
        </div>
        <Link
          to={ctaTo}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl px-7 py-4 text-sm font-black shadow-md transition hover:scale-[1.02]"
          style={{ background: color, color: foregroundOnSolidAccent(color) }}
        >
          Register Free
          <ArrowRight size={15} aria-hidden />
        </Link>
      </div>
    </section>
  </div>
);

export const DebtFinancialLegalMediaTab = ({ data, color }) => {
  const videos = data.videos ?? [];
  const podcasts = data.podcasts ?? [];
  const total = videos.length + podcasts.length;

  return (
    <div className="min-w-0 space-y-10 sm:space-y-12">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionHead
          eyebrow="Watch &amp; listen"
          title="Videos &amp; podcasts"
          subtitle={data.editorialPlaybook.videos.join(" · ")}
          color={color}
        />
        <span className="mt-1 shrink-0 rounded-lg px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {total} featured
        </span>
      </div>

      {videos.length > 0 ? (
        <section aria-labelledby="dfl-media-videos-heading" className="min-w-0">
          <MediaCarousel
            variant="video"
            color={color}
            ariaLabel="Debt and financial legal videos"
            items={videos}
            header={<h3 id="dfl-media-videos-heading" className="text-lg font-black text-slate-950 sm:text-xl">Videos</h3>}
            meta={`${videos.length} in library`}
            renderItem={(video) => <VideoCard video={video} color={color} />}
          />
        </section>
      ) : null}

      {podcasts.length > 0 ? (
        <section aria-labelledby="dfl-media-podcasts-heading" className="min-w-0">
          <MediaCarousel
            variant="podcast"
            color={color}
            ariaLabel="Debt and financial legal podcast episodes"
            items={podcasts}
            header={<h3 id="dfl-media-podcasts-heading" className="text-lg font-black text-slate-950 sm:text-xl">Podcasts</h3>}
            meta={`${podcasts.length} episodes`}
            renderItem={(episode) => <PodcastCard podcast={episode} color={color} />}
          />
          <p className="mt-4 px-2 text-center text-xs leading-relaxed text-gray-400 sm:text-left sm:text-sm md:mt-6">
            Listen links appear here when Spotify, Apple Podcasts, or other feeds are connected from admin or your CMS.
          </p>
        </section>
      ) : null}

      <div className="overflow-hidden rounded-xl bg-slate-950 text-white shadow-lg sm:rounded-[1.5rem]">
        <div className="grid gap-0 lg:grid-cols-2">
          <div className="p-6 sm:p-8 md:p-10">
            <div className="mb-4 inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em]">
              <Sparkles size={11} style={{ color }} aria-hidden />
              For partners
            </div>
            <h3 className="text-xl font-black leading-snug sm:text-2xl">{data.funnelApplication.conversion.text}</h3>
            <p className="mt-3 text-sm font-bold text-white/85">{data.primaryCtaHeading}</p>
            <Link
              to="/partners/onboard"
              className="mt-6 inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm font-black"
              style={{ background: color, color: foregroundOnSolidAccent(color) }}
            >
              Become a partner
              <ArrowRight size={14} aria-hidden />
            </Link>
          </div>
          <div className="relative min-h-[12rem] sm:min-h-[14rem] lg:min-h-0">
            <img src={data.galleryImages[1]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-slate-950/85" />
          </div>
        </div>
      </div>
    </div>
  );
};
